import {ChangeDetectorRef, Component, NgZone} from '@angular/core';
import {VerificationService} from './verification/verification.service';
import {RequestsService} from './requests/requests.service';

import {LoginService} from './login/login.service';
import {catchError, EMPTY, firstValueFrom, interval, takeWhile} from 'rxjs';
import {FormBuilder} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';

@Component({
	selector: 'mmmi-home',
	templateUrl: './home.component.html',
	styleUrls: ['./home.component.scss']
})
export class HomeComponent {
	// Rate at which to fetch messages in ms
	static readonly MESSAGE_FETCH_INTERVAL = 1000;

	// Rate at which to refresh expiry timer in ms
	static readonly EXPIRY_REFRESH_INTERVAL = 1000;

	// Session storage keys
	static readonly MMMI_LAST_FETCHED = 'mmmi_last_fetched';

	// user ID input
	verificationForm = this.formBuilder.group({
		userId: null
	});

	public readonly customPatterns = {i: {pattern: /[a-z0-9]/}};
	public readonly userIdMask = 'iii-iii-i';

	expiry?: number;

	constructor(
		private readonly zone: NgZone,
		private readonly formBuilder: FormBuilder,
		private readonly changeDetector: ChangeDetectorRef,
		private readonly translateService: TranslateService,

		private readonly loginService: LoginService,
		private readonly verificationService: VerificationService,
		private readonly requestsService: RequestsService
	) {
		this.setExpiryRefreshTask();
		this.setMessageFetchTask();
	}

	get identity() {
		return this.loginService.id;
	}

	async userIdPlaceholder() {
		return (await firstValueFrom(this.translateService.get('i18n.mmmi.verification.status.columns.remoteid')))[
			'i18n.mmmi.verification.status.columns.remoteid'
		];
	}

	login() {
		void this.loginService.login();
	}

	// TODO: Beautify user ID input - automatically add dashes
	async verify() {
		await this.verificationService.verify(this.verificationForm.value.userId);
		this.verificationForm.reset({userId: null});
	}

	// TODO: Refresh upon expiry
	private setExpiryRefreshTask() {
		interval(HomeComponent.EXPIRY_REFRESH_INTERVAL).subscribe(() => {
			if (this.loginService.expiry != null) {
				this.expiry = Math.max(0, Math.round((this.loginService.expiry - Date.now()) / 1000));
			} else {
				this.expiry = undefined;
			}
		});
	}

	private setMessageFetchTask() {
		this.zone.runOutsideAngular(() => {
			// run outside of Angular to avoid constant re-rendering
			interval(HomeComponent.MESSAGE_FETCH_INTERVAL)
				.pipe(
					takeWhile(() => this.loginService.id != null),
					catchError(error => {
						console.error('Error in message fetch interval:', error);
						return EMPTY; // Stops further execution while preventing app crash
					})
				)
				.subscribe({
					next: () => {
						if (this.loginService.id != null && this.loginService.apiToken != null) {
							const lastFetched = sessionStorage.getItem(HomeComponent.MMMI_LAST_FETCHED);
							this.requestsService
								.getMessages(this.loginService.apiToken, { userId: this.loginService.id }, lastFetched)
								.pipe(
									catchError(error => {
										console.error('Error retrieving messages:', error);
										return EMPTY; // Prevents subscription from stopping on error
									})
								)
								.subscribe({
									next: messageSet => {
										console.info(`retrieved ${messageSet.messages.length} messages, nextFrom: ${messageSet.nextFrom}`);
	
										sessionStorage.setItem(HomeComponent.MMMI_LAST_FETCHED, messageSet.nextFrom.toString());
	
										// we need to call JSON.parse again here due to over-stringification
										for (const msg of messageSet.messages.map(m => JSON.parse(m))) {
											this.verificationService.handleMessage(msg);
										}
	
										// Manually trigger change detection since we are outside Angular zone
										this.changeDetector.detectChanges();
									}
								});
						}
					},
					error: err => console.error('Unhandled error in subscription:', err)
				});
		});
	}
}
